<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MediaLibraryAPI from 'dashboard/api/mediaLibrary';
import Button from 'dashboard/components-next/button/Button.vue';
import MediaLibraryPreview from './MediaLibraryPreview.vue';
import MediaProductEditor from './MediaProductEditor.vue';
const props = defineProps({
  product: { type: Object, required: true },
  taxonomy: { type: Object, required: true },
  initialTab: { type: String, default: 'media' },
  canEdit: Boolean,
});
const emit = defineEmits(['saved', 'add', 'edit-media', 'download', 'busy']);
const { t } = useI18n();
const tab = ref(props.initialTab);
const items = ref([]);
const total = ref(0);
const page = ref(1);
const loading = ref(false);
const error = ref('');
let disposed = false;
async function load() {
  loading.value = true;
  error.value = '';
  try {
    const { data } = await MediaLibraryAPI.search({
      product_id: props.product.id,
      page: page.value,
    });
    if (disposed) return;
    items.value = data.items;
    total.value = data.total;
  } catch {
    if (!disposed) error.value = t('MEDIA_LIBRARY.ERROR');
  } finally {
    loading.value = false;
  }
}
function saved(product) {
  emit('saved', product);
  tab.value = 'media';
}
onMounted(load);
onBeforeUnmount(() => {
  disposed = true;
});
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex gap-1" role="group" :aria-label="product.name">
        <Button
          :label="t('MEDIA_LIBRARY.PRODUCT_MEDIA')"
          icon="i-lucide-images"
          :variant="tab === 'media' ? 'faded' : 'ghost'"
          color="slate"
          :aria-pressed="tab === 'media'"
          @click="tab = 'media'"
        />
        <Button
          v-if="canEdit"
          :label="t('MEDIA_LIBRARY.PRODUCT_DETAILS')"
          icon="i-lucide-pencil"
          :variant="tab === 'details' ? 'faded' : 'ghost'"
          color="slate"
          :aria-pressed="tab === 'details'"
          @click="tab = 'details'"
        />
      </div>
      <Button
        v-if="canEdit && tab === 'media'"
        icon="i-lucide-plus"
        :label="t('MEDIA_LIBRARY.ADD')"
        size="sm"
        @click="emit('add')"
      />
    </div>
    <MediaProductEditor
      v-if="tab === 'details' && canEdit"
      :product="product"
      :taxonomy="taxonomy"
      @saved="saved"
      @cancel="tab = 'media'"
      @busy="emit('busy', $event)"
    />
    <template v-else>
      <div
        v-if="product.checklist?.required.length"
        class="flex flex-col gap-2 border-b border-n-weak pb-4"
      >
        <p class="m-0 text-xs text-n-slate-11">
          {{
            t('MEDIA_LIBRARY.EXPECTED_PHOTOS', {
              covered: product.checklist.covered.length,
              total: product.checklist.required.length,
            })
          }}
        </p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="kind in product.checklist.required"
            :key="kind"
            class="flex items-center gap-1 rounded-md bg-n-alpha-1 px-2 py-1 text-xs text-n-slate-11"
            ><span
              :class="
                product.checklist.covered.includes(kind)
                  ? 'i-lucide-check'
                  : 'i-lucide-circle-dashed'
              "
              aria-hidden="true"
            />{{ kind }}
          </span>
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('MEDIA_LIBRARY.TAXONOMY.CHECKLIST_HELP') }}
        </p>
      </div>
      <p v-if="loading" role="status" class="text-sm text-n-slate-11">
        {{ t('MEDIA_LIBRARY.LOADING') }}
      </p>
      <p v-else-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <div v-else-if="!items.length" class="py-10 text-center">
        <span class="i-lucide-images size-8 text-n-slate-10" />
        <p class="mt-3 text-sm text-n-slate-12">
          {{ t('MEDIA_LIBRARY.NO_MEDIA') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('MEDIA_LIBRARY.ADD_FIRST_MEDIA') }}
        </p>
      </div>
      <div v-else class="flex flex-col divide-y divide-n-weak">
        <article
          v-for="item in items"
          :key="item.id"
          class="flex flex-col gap-3 py-4"
        >
          <MediaLibraryPreview
            :item="item"
            class="h-56 w-full border border-n-weak !bg-n-field"
          />
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h3 class="m-0 break-words text-sm font-medium">
                {{ item.title }}
              </h3>
              <p class="mb-0 mt-1 text-xs text-n-slate-11">
                {{ item.photo_kind }} ·
                {{ t('MEDIA_LIBRARY.STATUS.' + item.status) }}
              </p>
            </div>
            <div class="flex shrink-0 gap-1">
              <Button
                icon="i-lucide-download"
                variant="outline"
                color="slate"
                :aria-label="t('MEDIA_LIBRARY.DOWNLOAD')"
                :title="t('MEDIA_LIBRARY.DOWNLOAD')"
                @click="emit('download', item)"
              /><Button
                v-if="canEdit"
                icon="i-lucide-pencil"
                variant="outline"
                color="slate"
                :aria-label="t('MEDIA_LIBRARY.EDIT')"
                :title="t('MEDIA_LIBRARY.EDIT')"
                @click="emit('edit-media', item)"
              />
            </div>
          </div>
          <p
            v-if="item.description"
            class="m-0 whitespace-pre-wrap break-words text-sm text-n-slate-11"
          >
            {{ item.description }}
          </p>
          <div v-if="item.tags?.length" class="flex flex-wrap gap-1">
            <span
              v-for="tag in item.tags"
              :key="tag"
              class="rounded-md bg-n-blue-2 px-2 py-1 text-xs text-n-blue-11"
            >
              {{ tag }}
            </span>
          </div>
          <p class="m-0 break-all text-xs text-n-slate-11">
            {{ item.filename }} ·
            {{
              t('MEDIA_LIBRARY.SIZE', {
                size: (item.byte_size / 1048576).toFixed(2),
              })
            }}
          </p>
        </article>
      </div>
      <nav
        v-if="total > 30"
        class="flex items-center justify-between gap-2"
        :aria-label="t('MEDIA_LIBRARY.PAGE', { page })"
      >
        <Button
          variant="outline"
          :label="t('MEDIA_LIBRARY.PREVIOUS')"
          :disabled="loading || page === 1"
          @click="
            page--;
            load();
          "
        />
        <span class="text-xs text-n-slate-11">
          {{ t('MEDIA_LIBRARY.PAGE', { page }) }}
        </span>
        <Button
          variant="outline"
          :label="t('MEDIA_LIBRARY.NEXT')"
          :disabled="loading || page * 30 >= total"
          @click="
            page++;
            load();
          "
        />
      </nav>
    </template>
  </div>
</template>
